import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import AppField from "../../design-system/components/AppField";
import AppFieldStack from "../../design-system/components/AppFieldStack";
import AppInput from "../../design-system/components/AppInput";
import AppLoadingOverlay from "../../design-system/components/AppLoadingOverlay";
import AppSelect from "../../design-system/components/AppSelect";
import AppText from "../../design-system/components/AppText";
import {
  IconAdd,
  IconChevronLeft,
  IconChevronRight,
  IconDelete,
  IconHoliday,
  IconSuccess,
  IconTime,
  IconTimetable,
} from "../../design-system/icons";
// Dasselbe Symbol wie in /settings für "Abteilungen & Schichten".
import IconDepartment from "@mui/icons-material/CorporateFareOutlined";
import { normalizeTimeInput } from "../../utils/timeInput";
import TypeSettingsContent from "./TypeSettingsStep";
import ColorButton from "./ColorButton";
import {
  getInitialSetupDepartments,
  getInitialSetupOpeningDays,
  getInitialSetupShifts,
  updateInitialSetupDepartments,
  updateInitialSetupOpeningDays,
  updateInitialSetupShifts,
} from "./api";

import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "../settings/settings.css";
import "../shiftplan_v2/styles/base.css";
import "../shiftplan_v2/styles/panels.css";
import "./initialSetup.css";

const COLORS = ["#4CCEAC", "#0E7490", "#2563EB", "#64748B", "#7C3AED", "#DB2777"];
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const SHORTHAND_MAX_LENGTH = 4;
const DEPARTMENT_PRESETS = [
  { name: "Admin", shorthand_symbol: "ADM", label: "Admin" },
  { name: "Service", shorthand_symbol: "SER", label: "Service" },
  { name: "Front Office", shorthand_symbol: "FO", label: "Front Office" },
  { name: "Back Office", shorthand_symbol: "BO", label: "Back Office" },
];
const SHIFT_PRESETS = [
  { name: "Frühschicht", shorthand_symbol: "FS", start_time: "06:00", end_time: "15:00", label: "Frühschicht" },
  { name: "Spätschicht", shorthand_symbol: "SPS", start_time: "14:00", end_time: "22:00", label: "Spätschicht" },
  { name: "Nachtschicht", shorthand_symbol: "NS", start_time: "22:00", end_time: "06:00", label: "Nachtschicht" },
];

let temporaryId = 0;

function nextTemporaryId(prefix) {
  temporaryId += 1;
  return `${prefix}-${Date.now()}-${temporaryId}`;
}

function emptyDepartment(index = 0) {
  return {
    id: null,
    client_id: nextTemporaryId("department"),
    name: "",
    shorthand_symbol: "",
    shorthand_manual: false,
    color_hex: COLORS[index % COLORS.length],
    is_active: true,
    user_ids: [],
  };
}

function emptyShift(index = 0) {
  return {
    id: null,
    client_id: nextTemporaryId("shift"),
    name: "",
    shorthand_symbol: "",
    shorthand_manual: false,
    description: "",
    color_hex: COLORS[index % COLORS.length],
    start_time: "",
    end_time: "",
    break_duration_minutes: "",
    department_ids: [],
    is_active: true,
  };
}

function stripClientFields(items) {
  return (items || []).map(({ client_id, shorthand_manual, _removed, _persistedInactive, ...item }) => item);
}

function keepInactiveRowsHidden(items) {
  return (items || []).map((item) =>
    item.is_active === false ? { ...item, _removed: true, _persistedInactive: true } : item
  );
}

function prepareSetupRows(items, hasContent) {
  const candidates = (items || []).filter((item) =>
    !(item._persistedInactive && item.is_active === false) && hasContent(item)
  );
  const activeRows = candidates.filter((item) => item.is_active !== false);
  return candidates.filter((item) => {
    if (item.is_active !== false) return true;
    const name = String(item.name || "").trim().toLowerCase();
    const shorthand = String(item.shorthand_symbol || "").trim().toLowerCase();
    return !activeRows.some((active) =>
      (name && String(active.name || "").trim().toLowerCase() === name) ||
      (shorthand && String(active.shorthand_symbol || "").trim().toLowerCase() === shorthand)
    );
  });
}

function normalizeShorthand(value) {
  return String(value || "")
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase()
    .slice(0, SHORTHAND_MAX_LENGTH);
}

function nameWords(name) {
  return String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

function generateUniqueShorthand(name, items, currentIndex) {
  const used = new Set(
    items
      .filter((_item, index) => index !== currentIndex)
      .map((item) => normalizeShorthand(item.shorthand_symbol))
      .filter(Boolean)
  );
  const words = nameWords(name);
  if (!words.length) return "";

  const candidates = [
    words.map((word) => word[0]).join(""),
    words.join("").slice(0, SHORTHAND_MAX_LENGTH),
  ].map(normalizeShorthand);

  const available = candidates.find((candidate) => candidate && !used.has(candidate));
  if (available) return available;

  const base = candidates[0] || "X";
  for (let suffix = 2; suffix < 1000; suffix += 1) {
    const suffixText = String(suffix);
    const candidate = `${base.slice(0, SHORTHAND_MAX_LENGTH - suffixText.length)}${suffixText}`;
    if (!used.has(candidate)) return candidate;
  }
  return base;
}

function nextColor(items) {
  const used = new Set((items || []).map((item) => item.color_hex).filter(Boolean));
  return COLORS.find((color) => !used.has(color)) || COLORS[(items || []).length % COLORS.length];
}

function weekdayLabel(weekday, language) {
  const date = new Date(2024, 0, 1 + Number(weekday || 0));
  return new Intl.DateTimeFormat(language?.startsWith("en") ? "en-GB" : "de-CH", {
    weekday: "long",
  }).format(date);
}

function duplicateValues(items, key) {
  const values = (items || [])
    .map((item) => String(item[key] || "").trim().toLowerCase())
    .filter(Boolean);
  return values.length !== new Set(values).size;
}

function SetupAgenda({ currentStep, steps }) {
  return (
    <Box
      component="ol"
      aria-label="Setup"
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        p: 0,
        m: 0,
        overflowX: "auto",
        listStyle: "none",
      }}
    >
      {steps.map((step, index) => {
        const complete = index < currentStep;
        const active = index === currentStep;
        return (
          <React.Fragment key={step.id}>
            <Box
              component="li"
              aria-current={active ? "step" : undefined}
              sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
            >
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: complete ? "var(--ds-color-success-default)" : active ? "var(--ds-color-text-primary)" : "var(--ds-color-surface-subtle)",
                  color: complete || active ? "var(--ds-color-text-inverse)" : "var(--ds-color-text-tertiary)",
                  border: complete || active ? "none" : "1px solid var(--ds-color-border-subtle)",
                  fontSize: 12,
                  fontWeight: 800,
                  lineHeight: 1,
                  "& svg": { display: "block" },
                }}
              >
                {complete ? <IconSuccess sx={{ fontSize: 15 }} /> : index + 1}
              </Box>
              <AppText as="span" variant="body-3" tone={active || complete ? "primary" : "tertiary"} style={{ fontWeight: active ? 800 : 600 }}>
                {step.label}
              </AppText>
              {step.optional ? (
                <AppText as="span" variant="body-3" tone="tertiary" style={{ fontStyle: "italic" }}>
                  {step.optionalLabel}
                </AppText>
              ) : null}
            </Box>
            {index < steps.length - 1 ? (
              <Box
                aria-hidden="true"
                sx={{
                  minWidth: 28,
                  maxWidth: 72,
                  height: 2,
                  mx: 1.25,
                  flex: "1 1 48px",
                  bgcolor: "var(--ds-palette-neutral-300)",
                }}
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

function PresetButtons({ presets, existingNames, onSelect, label }) {
  const existing = new Set((existingNames || []).map((name) => String(name || "").trim().toLowerCase()));

  return (
    <Box className="initial-setup-presets" aria-label={label}>
      {(presets || []).map((preset) => {
        const alreadyAdded = existing.has(preset.name.toLowerCase());
        return (
          <AppButton
            key={preset.name}
            variant="outlined"
            size="sm"
            className="initial-setup-preset"
            disabled={alreadyAdded}
            onClick={() => onSelect(preset)}
          >
            {alreadyAdded ? <IconSuccess sx={{ fontSize: 17 }} /> : "+"} {preset.label}
          </AppButton>
        );
      })}
    </Box>
  );
}

function OpeningDaysStep({ days, setDays, language, t }) {
  return (
    <AppFieldStack>
      <div className="company-opening-days-grid">
        {days.map((day, index) => {
          const label = weekdayLabel(day.weekday, language);
          return (
            <AppButton
              key={day.weekday}
              variant={day.is_open ? "primary" : "outlined"}
              size="sm"
              className="company-opening-day-button"
              aria-pressed={day.is_open}
              aria-label={`${label}: ${t(day.is_open ? "initial_setup.open" : "initial_setup.closed")}`}
              onClick={() =>
                setDays((current) =>
                  current.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, is_open: !item.is_open } : item
                  )
                )
              }
            >
              <span className="company-opening-day-button__label company-opening-day-button__label--full">{label}</span>
              <span className="company-opening-day-button__label company-opening-day-button__label--short" aria-hidden="true">{label.slice(0, 2)}</span>
              <span className="company-opening-day-button__status">{t(day.is_open ? "initial_setup.open" : "initial_setup.closed")}</span>
            </AppButton>
          );
        })}
      </div>
    </AppFieldStack>
  );
}

function DepartmentsStep({ departments, setDepartments, setShifts, t, presets, onPreset }) {
  const update = (index, key, value) => {
    setDepartments((current) =>
      current.map((department, itemIndex) => {
        if (itemIndex !== index) return department;
        if (key === "name") {
          return {
            ...department,
            name: value,
            shorthand_symbol: department.shorthand_manual
              ? department.shorthand_symbol
              : generateUniqueShorthand(value, current, index),
          };
        }
        if (key === "shorthand_symbol") {
          return { ...department, shorthand_symbol: normalizeShorthand(value), shorthand_manual: true };
        }
        return { ...department, [key]: value };
      })
    );
  };

  const removeDepartment = (index) => {
    const target = departments[index];
    setDepartments((current) =>
      target?.id
        ? current.map((department, itemIndex) =>
            itemIndex === index ? { ...department, is_active: false, _removed: true, user_ids: [] } : department
          )
        : current.filter((_department, itemIndex) => itemIndex !== index)
    );
    if (target?.id) {
      setShifts((current) =>
        current.map((shift) => ({
          ...shift,
          department_ids: (shift.department_ids || []).filter(
            (departmentId) => Number(departmentId) !== Number(target.id)
          ),
        }))
      );
    }
  };

  const visibleDepartments = departments
    .map((department, index) => ({ department, index }))
    .filter(({ department }) => !department._removed);

  return (
    <Stack spacing={2}>
      <PresetButtons
        presets={presets}
        existingNames={visibleDepartments.map(({ department }) => department.name)}
        onSelect={onPreset}
        label={t("initial_setup.department_examples")}
      />
      {visibleDepartments.map(({ department, index }) => (
        <AppCard key={department.id || department.client_id}>
          <AppCardBody>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1fr) minmax(110px, 180px) 64px 36px",
                },
                gap: 1.5,
                alignItems: "end",
              }}
            >
              <AppField label={t("settings_ui.name")} required>
                <AppInput value={department.name || ""} onChange={(event) => update(index, "name", event.target.value)} />
              </AppField>
              <AppField label={t("settings_ui.abbreviation")} required>
                <AppInput maxLength={SHORTHAND_MAX_LENGTH} value={department.shorthand_symbol || ""} onChange={(event) => update(index, "shorthand_symbol", event.target.value)} />
              </AppField>
              <AppField label={t("settings_ui.color")}>
                <ColorButton value={department.color_hex} label={department.name || t("initial_setup.departments")} onChange={(color) => update(index, "color_hex", color)} />
              </AppField>
              <AppButton
                variant="neutral"
                size="sm"
                iconOnly
                aria-label={t("initial_setup.remove_department")}
                onClick={() => removeDepartment(index)}
              >
                <IconDelete />
              </AppButton>
            </Box>
          </AppCardBody>
        </AppCard>
      ))}
    </Stack>
  );
}

function ShiftsStep({ shifts, setShifts, departments, t, presets, onPreset }) {
  const activeDepartments = departments.filter(
    (department) => !department._removed && department.is_active !== false && department.id
  );
  const options = activeDepartments.map((department) => ({ value: department.id, label: department.name }));
  const update = (index, key, value) => {
    const nextValue = key === "start_time" || key === "end_time"
      ? normalizeTimeInput(value)
      : key === "department_ids"
        ? (value || []).map(Number)
        : key === "break_duration_minutes"
          ? String(value || "").replace(/[^\d]/g, "").slice(0, 3)
          : value;
    setShifts((current) =>
      current.map((shift, itemIndex) => {
        if (itemIndex !== index) return shift;
        if (key === "name") {
          return {
            ...shift,
            name: nextValue,
            shorthand_symbol: shift.shorthand_manual
              ? shift.shorthand_symbol
              : generateUniqueShorthand(nextValue, current, index),
          };
        }
        if (key === "shorthand_symbol") {
          return { ...shift, shorthand_symbol: normalizeShorthand(nextValue), shorthand_manual: true };
        }
        return { ...shift, [key]: nextValue };
      })
    );
  };

  const removeShift = (index) => {
    const target = shifts[index];
    setShifts((current) =>
      target?.id
        ? current.map((shift, itemIndex) =>
            itemIndex === index
              ? { ...shift, is_active: false, _removed: true, department_ids: [] }
              : shift
          )
        : current.filter((_shift, itemIndex) => itemIndex !== index)
    );
  };

  const visibleShifts = shifts
    .map((shift, index) => ({ shift, index }))
    .filter(({ shift }) => !shift._removed);

  return (
    <Stack spacing={2}>
      <PresetButtons
        presets={presets}
        existingNames={visibleShifts.map(({ shift }) => shift.name)}
        onSelect={onPreset}
        label={t("initial_setup.shift_examples")}
      />
      {visibleShifts.map(({ shift, index }) => (
        <AppCard key={shift.id || shift.client_id}>
          <AppCardBody>
            <AppFieldStack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(0, 1fr) minmax(110px, 180px) 64px 36px",
                  },
                  gap: 1.5,
                  alignItems: "end",
                }}
              >
                <AppField label={t("settings_ui.name")} required><AppInput value={shift.name || ""} onChange={(event) => update(index, "name", event.target.value)} /></AppField>
                <AppField label={t("settings_ui.abbreviation")} required><AppInput maxLength={SHORTHAND_MAX_LENGTH} value={shift.shorthand_symbol || ""} onChange={(event) => update(index, "shorthand_symbol", event.target.value)} /></AppField>
                <AppField label={t("settings_ui.color")}>
                  <ColorButton value={shift.color_hex} label={shift.name || t("initial_setup.shifts")} onChange={(color) => update(index, "color_hex", color)} />
                </AppField>
                <AppButton variant="neutral" size="sm" iconOnly aria-label={t("initial_setup.remove_shift")} onClick={() => removeShift(index)}>
                  <IconDelete />
                </AppButton>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(220px, 1.5fr) repeat(3, minmax(100px, .6fr))",
                  },
                  gap: 1.5,
                  alignItems: "start",
                }}
              >
                <AppField label={t("settings_ui.departments")} required error={shift.is_active !== false && !(shift.department_ids || []).length ? t("initial_setup.department_required") : undefined}>
                  <AppSelect
                    options={options}
                    multiple
                    searchable
                    showSelectAll
                    value={shift.department_ids || []}
                    invalid={shift.is_active !== false && !(shift.department_ids || []).length}
                    onChange={(event) => update(index, "department_ids", event.target.value)}
                    disabled={shift.is_active === false}
                    placeholder={t("settings_ui.add_department")}
                  />
                </AppField>
                <AppField label={t("settings_ui.start")} required error={shift.start_time && !TIME_RE.test(shift.start_time) ? t("settings_ui.time_format_error") : undefined}>
                  <AppInput maxLength={5} inputMode="numeric" placeholder="HH:MM" invalid={Boolean(shift.start_time) && !TIME_RE.test(shift.start_time)} value={shift.start_time || ""} onChange={(event) => update(index, "start_time", event.target.value)} />
                </AppField>
                <AppField label={t("settings_ui.end")} required error={shift.end_time && !TIME_RE.test(shift.end_time) ? t("settings_ui.time_format_error") : undefined}>
                  <AppInput maxLength={5} inputMode="numeric" placeholder="HH:MM" invalid={Boolean(shift.end_time) && !TIME_RE.test(shift.end_time)} value={shift.end_time || ""} onChange={(event) => update(index, "end_time", event.target.value)} />
                </AppField>
                <AppField label={t("settings_ui.break_minutes")}><AppInput value={shift.break_duration_minutes ?? ""} onChange={(event) => update(index, "break_duration_minutes", event.target.value)} /></AppField>
              </Box>
            </AppFieldStack>
          </AppCardBody>
        </AppCard>
      ))}
    </Stack>
  );
}

export default function InitialSetup() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [openingDays, setOpeningDays] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [shifts, setShifts] = useState([]);

  const steps = useMemo(() => [
    { id: "opening", label: t("initial_setup.opening_days"), icon: <IconTimetable /> },
    { id: "departments", label: t("initial_setup.departments"), icon: <IconDepartment /> },
    { id: "shifts", label: t("initial_setup.shifts"), icon: <IconTime /> },
    { id: "holidays", label: t("initial_setup.holiday_types"), icon: <IconHoliday />, optional: true, optionalLabel: t("initial_setup.optional") },
    { id: "absences", label: t("initial_setup.absence_types"), icon: <IconHoliday />, optional: true, optionalLabel: t("initial_setup.optional") },
  ], [t]);

  const loadSetupData = useCallback(async () => {
    const [openingResponse, departmentResponse, shiftResponse] = await Promise.all([
      getInitialSetupOpeningDays(),
      getInitialSetupDepartments(),
      getInitialSetupShifts(),
    ]);
    setOpeningDays(Array.isArray(openingResponse.opening_days) ? openingResponse.opening_days : []);
    const companyDepartments = keepInactiveRowsHidden(departmentResponse.departments);
    const companyShifts = keepInactiveRowsHidden(shiftResponse.shifts);
    const hasActiveDepartments = companyDepartments.some((department) => department.is_active !== false);
    const hasActiveShifts = companyShifts.some((shift) => shift.is_active !== false);
    setDepartments(hasActiveDepartments ? companyDepartments : [...companyDepartments, emptyDepartment()]);
    setShifts(hasActiveShifts ? companyShifts : [...companyShifts, emptyShift()]);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadSetupData()
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.message || t("initial_setup.load_error"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [loadSetupData, t]);

  useEffect(() => {
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  const addDepartment = () => {
    setDepartments((current) => [
      ...current,
      { ...emptyDepartment(current.length), color_hex: nextColor(current) },
    ]);
  };

  const addDepartmentPreset = (preset) => {
    setDepartments((current) => {
      const next = {
        ...emptyDepartment(current.length),
        name: preset.name,
        shorthand_symbol: preset.shorthand_symbol,
        color_hex: nextColor(current),
        shorthand_manual: true,
      };
      const hasOnlyBlankRow = current.length === 1 && !current[0].name && !current[0].shorthand_symbol;
      return hasOnlyBlankRow ? [next] : [...current, next];
    });
  };

  const addShift = () => {
    setShifts((current) => [
      ...current,
      { ...emptyShift(current.length), color_hex: nextColor(current) },
    ]);
  };

  const addShiftPreset = (preset) => {
    const departmentIds = departments
      .filter((department) => department.is_active !== false && department.id)
      .map((department) => Number(department.id));
    setShifts((current) => {
      const next = {
        ...emptyShift(current.length),
        name: preset.name,
        shorthand_symbol: preset.shorthand_symbol,
        start_time: preset.start_time,
        end_time: preset.end_time,
        color_hex: nextColor(current),
        department_ids: departmentIds,
        shorthand_manual: true,
      };
      const hasOnlyBlankRow = current.length === 1 && !current[0].name && !current[0].shorthand_symbol;
      return hasOnlyBlankRow ? [next] : [...current, next];
    });
  };

  const saveOpeningDays = async () => {
    await updateInitialSetupOpeningDays(openingDays);
  };

  const saveDepartments = async () => {
    const rows = prepareSetupRows(
      departments,
      (item) => item.name.trim() || item.shorthand_symbol.trim()
    );
    if (!rows.length || rows.some((item) => !item.name.trim() || !item.shorthand_symbol.trim())) {
      throw new Error(t("initial_setup.departments_required"));
    }
    if (duplicateValues(rows, "name") || duplicateValues(rows, "shorthand_symbol")) {
      throw new Error(t("initial_setup.unique_abbreviations"));
    }
    if (!rows.some((item) => item.is_active !== false)) {
      throw new Error(t("initial_setup.active_department_required"));
    }
    await updateInitialSetupDepartments(stripClientFields(rows));
    await loadSetupData();
  };

  const saveShifts = async () => {
    const rows = prepareSetupRows(
      shifts,
      (item) => item.name.trim() || item.shorthand_symbol.trim() || item.start_time || item.end_time
    );
    const invalid = !rows.length || rows.some((item) =>
      !item.name.trim() ||
      !item.shorthand_symbol.trim() ||
      !TIME_RE.test(item.start_time) ||
      !TIME_RE.test(item.end_time) ||
      (item.is_active !== false && !(item.department_ids || []).length)
    );
    if (invalid) throw new Error(t("initial_setup.shifts_required"));
    if (duplicateValues(rows, "name") || duplicateValues(rows, "shorthand_symbol")) {
      throw new Error(t("initial_setup.unique_abbreviations"));
    }
    await updateInitialSetupShifts(stripClientFields(rows));
    await loadSetupData();
  };

  const goForward = async () => {
    if (currentStep >= steps.length - 1) {
      navigate("/admin/dashboard");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (currentStep === 0) await saveOpeningDays();
      if (currentStep === 1) await saveDepartments();
      if (currentStep === 2) await saveShifts();
      setCurrentStep((step) => step + 1);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError?.message || t("initial_setup.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    if (currentStep === 0) {
      navigate("/welcome?choose=1");
      return;
    }
    setCurrentStep((step) => step - 1);
  };

  const step = steps[currentStep];
  const optionalStep = Boolean(step?.optional);

  return (
    <Box sx={{ minHeight: "100%", bgcolor: "var(--ds-color-surface-subtle)", px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>
      <AppLoadingOverlay open={loading} label={t("notification.loading")} />
      <Stack spacing={3} sx={{ width: "100%", maxWidth: 1180, mx: "auto" }}>
        <Box component="img" src="/assets/TimeTab_Logo_quer_cmyk_pos.png" alt={t("initial_setup.logo_alt")} sx={{ width: { xs: 160, md: 190 }, height: "auto", mx: "auto" }} />

        <AppCard>
          <AppCardBody>
            <SetupAgenda currentStep={currentStep} steps={steps} />
          </AppCardBody>
        </AppCard>

        {error ? <AppCallout variant="error" description={error} /> : null}

        {currentStep <= 2 ? (
          <AppCard>
            <AppCardHeader
              title={step?.label}
              visual={step?.icon}
              action={currentStep === 1 ? (
                <AppButton variant="primary" size="sm" onClick={addDepartment}>
                  <IconAdd />
                  {t("initial_setup.add_department")}
                </AppButton>
              ) : currentStep === 2 ? (
                <AppButton variant="primary" size="sm" onClick={addShift}>
                  <IconAdd />
                  {t("initial_setup.add_shift")}
                </AppButton>
              ) : null}
            />
            <AppCardBody>
              {currentStep === 0 ? <OpeningDaysStep days={openingDays} setDays={setOpeningDays} language={i18n.resolvedLanguage || i18n.language} t={t} /> : null}
              {currentStep === 1 ? (
                <DepartmentsStep
                  departments={departments}
                  setDepartments={setDepartments}
                  setShifts={setShifts}
                  presets={DEPARTMENT_PRESETS}
                  onPreset={addDepartmentPreset}
                  t={t}
                />
              ) : null}
              {currentStep === 2 ? (
                <ShiftsStep
                  shifts={shifts}
                  setShifts={setShifts}
                  departments={departments}
                  presets={SHIFT_PRESETS}
                  onPreset={addShiftPreset}
                  t={t}
                />
              ) : null}
            </AppCardBody>
          </AppCard>
        ) : (
          <AppCard className="initial-setup-type-card">
            <TypeSettingsContent
              active
              onboarding
              visibleKind={currentStep === 3 ? "holiday" : "absence"}
              showPresets
              cardHeader={{
                title: step?.label,
                visual: step?.icon,
                addLabel: t(currentStep === 3 ? "initial_setup.add_holiday_type" : "initial_setup.add_absence_type"),
              }}
            />
          </AppCard>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <AppButton variant="outlined" size="md" disabled={saving} onClick={goBack}>
            <IconChevronLeft />
            {t("initial_setup.back")}
          </AppButton>
          <Stack direction="row" spacing={1}>
            {optionalStep ? (
              <AppButton
                variant="muted"
                size="md"
                disabled={saving}
                onClick={() => currentStep === steps.length - 1 ? navigate("/admin/dashboard") : setCurrentStep((value) => value + 1)}
              >
                {t("initial_setup.skip")}
              </AppButton>
            ) : null}
            <AppButton variant="primary" size="md" disabled={saving || loading} onClick={goForward}>
              {saving ? t("initial_setup.saving") : currentStep === steps.length - 1 ? t("initial_setup.finish") : t("initial_setup.next")}
              {!saving && currentStep < steps.length - 1 ? <IconChevronRight /> : null}
            </AppButton>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
